import type { AccessTier, Sport } from '@sharpr/types';
import { AccessTierSchema } from '@sharpr/types/validation';
import { jwtDecode } from 'jwt-decode';
import { isIncludedIn, unique } from 'remeda';

// A viewer's access tier for a sport's paid data (@sharpr/types), and the one mapping both sides use: the server cuts every
// response by it (services/access getAccessTier, on live access); the client routes and renders by it (useAccess, a hint:
// the claim until the live status loads).
export type { AccessTier };

export const accessTier = ({ isAdmin, hasSport }: { isAdmin: boolean; hasSport: boolean }): AccessTier =>
  isAdmin ? 'admin' : hasSport ? 'subscriber' : 'public';

const RANK: Record<AccessTier, number> = { public: 0, subscriber: 1, admin: 2 };
// Whether tier `a` sees no more than tier `b`: a view may be served to anyone at or above it.
export const atOrBelow = (a: AccessTier, b: AccessTier): boolean => RANK[a] <= RANK[b];

// The view a request is served: the one it asks for (`?view=`: an admin previewing what subscribers see) when at or below
// its tier, else its tier. Never above. The board's seed and its downloads alike.
export function requestedView(tier: AccessTier, asked: string | null): AccessTier {
  const view = AccessTierSchema.safeParse(asked);
  return view.success && atOrBelow(view.data, tier) ? view.data : tier;
}

export interface SportsAccess {
  isAdmin: boolean;
  sportsAccess: Sport[];
}

const SPORTS = ['nba', 'mlb'] as const satisfies readonly Sport[];

// The access the access-token hook stamps on the JWT (`sports_access`, `is_admin`, from the same SQL rule as the server),
// read unverified: a hint. A token without them (the hook fails open), or one that doesn't decode, reads as no access.
export function accessFromToken(token: string | null | undefined): SportsAccess {
  let claims: { sports_access?: unknown; is_admin?: unknown } = {};
  try {
    const decoded: unknown = token ? jwtDecode(token) : null;
    if (decoded && typeof decoded === 'object') claims = decoded;
  } catch {
    // Malformed: no access.
  }
  const raw = claims.sports_access;
  return { sportsAccess: Array.isArray(raw) ? unique(raw.filter(isIncludedIn(SPORTS))) : [], isAdmin: claims.is_admin === true };
}
